import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { EquipmentTypes } from '@d-orca/shared/trusstor';
import { fetchFloorViewSvg } from '@apis/storage';
import { FloorSvg } from '@components/FloorSvg/FloorSvg';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { getEquipmentIconFromType } from '@utils/equipmentIcons';
import { readSvgAnchorCentres, tightenSvgViewBox } from '@utils/svgMarkers';
import type { IEquipmentAsset } from '../../equipment.types';
import { FloorPlanSkeleton } from './FloorPlanSkeleton/FloorPlanSkeleton';
import styles from './EquipmentFloorPlan.module.css';
import panelStyles from '../EquipmentSiteMap.module.css';

interface IEquipmentFloorPlanProps {
  siteId: string;
  floorId: string;
  /** The selected asset — its `areaId` drives the tint + chip. */
  asset: IEquipmentAsset;
}

/**
 * Floor plan for the Equipment site map: renders the shared `<FloorSvg>`
 * primitive and layers the monitor's floor-view language onto it for the
 * *single* selected asset — a blue tint on the asset's area plus one
 * equipment chip anchored at that area's `workers<id>` marker.
 *
 * Shares the floor-plan mechanics with `MonitorPage/MainView/FloorDrawer`:
 * `FloorSvg`'s `highlightAreaId` prop tints the area, and `tightenSvgViewBox` +
 * `readSvgAnchorCentres` (both from `@utils/svgMarkers`) handle the viewBox +
 * anchor measurement. Scoped to one area/one chip — it does not aggregate every
 * asset on the floor; the registry selection is the subject.
 */
export function EquipmentFloorPlan({ siteId, floorId, asset }: IEquipmentFloorPlanProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  // The anchor is tagged with the target it was measured for. When the selected
  // asset switches (new floor/area) the target changes but the old anchor still
  // holds the previous area's coordinates for a frame — rendering the chip only
  // while `anchor.key === target` keeps it hidden until it's re-measured at the
  // new position, instead of flashing the new asset at the old spot.
  const [anchor, setAnchor] = useState<{ x: number; y: number; key: string } | null>(null);
  const areaId = asset.areaId;
  const target = `${siteId}|${floorId}|${areaId ?? ''}`;

  // Subscribe to the same floor-view query `<FloorSvg>` runs (shared via
  // queryKey) so this component re-measures once the SVG markup is actually
  // inlined — `FloorSvg` injects it asynchronously after the fetch resolves,
  // and (siteId, floorId) alone wouldn't re-trigger the effects below.
  const { data: svgText, isLoading, isError } = useQuery({
    queryKey: ['storage', 'floor-view', siteId, floorId],
    queryFn: ({ signal }) => fetchFloorViewSvg(siteId, floorId, signal),
    enabled: Boolean(siteId && floorId),
    staleTime: 5 * 60_000,
  });

  // Tighten the SVG viewBox to its content bbox so artboard whitespace doesn't
  // push the plan off-centre (shared with FloorDrawer).
  useEffect(() => {
    const svg = containerRef.current?.querySelector<SVGSVGElement>('svg');
    if (svg) tightenSvgViewBox(svg);
  }, [siteId, floorId, svgText]);

  // Measure the selected area's `workers<id>` anchor centre (container px) so the
  // chip sits on it, re-measuring on resize. The area tint itself is handled by
  // FloorSvg's `highlightAreaId` prop below.
  useEffect(() => {
    const root = containerRef.current;
    if (!root || !areaId) {
      setAnchor(null);
      return;
    }
    const measure = () => {
      const match = readSvgAnchorCentres(root, 'workers').find(
        (a) => a.areaId.toLowerCase() === areaId.toLowerCase(),
      );
      setAnchor(match ? { x: match.x, y: match.y, key: target } : null);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    return () => ro.disconnect();
  }, [siteId, floorId, areaId, svgText, target]);

  if (isLoading) {
    return <FloorPlanSkeleton label={t('equipment.map.loading')} />;
  }

  // `fetchFloorViewSvg` resolves to null on 404 — no plan is a valid, non-error state.
  if (isError || !svgText) {
    return <div className={panelStyles.placeholder}>{t('equipment.map.noPlan')}</div>;
  }

  return (
    <div className={styles.svgStack}>
      <FloorSvg
        ref={containerRef}
        siteId={siteId}
        floorId={floorId}
        {...(areaId ? { highlightAreaId: areaId } : {})}
        className={styles.svgWrap}
      />
      {anchor && anchor.key === target && (
        <div className={styles.overlay} aria-hidden="true">
          <div className={styles.chipStack} style={{ left: anchor.x, top: anchor.y }}>
            {/* Tooltip surfaces the full name when the chip label is truncated. */}
            <Tooltip content={asset.name} delay={0}>
              <span className={styles.equipmentChip}>
                <img
                  className={styles.equipmentChipIcon}
                  src={getEquipmentIconFromType(asset.type as EquipmentTypes)}
                  alt=""
                />
                <span className={styles.equipmentChipLabel}>{asset.name}</span>
              </span>
            </Tooltip>
          </div>
        </div>
      )}
    </div>
  );
}
